import { forwardRef, type ComponentProps, type ForwardedRef } from 'react';
import { twJoin } from 'tailwind-merge';

import { Link } from '@/core/routing/components/Link';
import { type TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';

type Props = Omit<ComponentProps<typeof Link>, 'className'> & {
  fitContent?: boolean;
  icon?: TamagoshiIconComponent;
  showIconRight?: boolean;
  size?: 'L' | 'M';
  text: string;
  variant: 'link' | 'primary' | 'secondary' | 'tertiary';
};

/**
 * A link that looks like a button.
 *
 * Not to be confused with `Button` which is an actual button. This component
 * renders an accessible link in the DOM, therefore disabled and loading status
 * don't make sense here.
 *
 * See the related [Figma](figma) for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15902-17958
 */
export const NavigationButton = forwardRef(
  (
    {
      fitContent,
      icon: Icon,
      showIconRight,
      size = 'L',
      text,
      variant,
      ...props
    }: Props,
    ref: ForwardedRef<HTMLAnchorElement>,
  ) => (
    <Link
      className={twJoin(
        `flex items-center justify-center gap-xs rounded-card text-left
        font-semibold`,
        fitContent || variant === 'link' ? 'w-fit' : 'w-full',
        showIconRight ? 'flex-row-reverse' : 'flex-row',
        size === 'L' ? 'text-body1' : 'text-body2',
        variant === 'link'
          ? 'underline hover:text-info-primary'
          : size === 'L'
            ? 'h-[48px] px-m'
            : 'h-[40px] px-s',
        variant === 'primary' &&
          `bg-accent-primary hover:bg-accent-primaryHover
          active:bg-accent-primaryActive`,
        variant === 'secondary' &&
          `border border-foundation-tertiary bg-foundation-inverted
          hover:bg-foundation-primary active:bg-foundation-secondary`,
        variant === 'tertiary' &&
          `border border-foundation-secondary bg-foundation-inverted
          hover:bg-foundation-primary active:bg-foundation-secondary`,
      )}
      ref={ref}
      {...props}
    >
      {Icon && <Icon />}
      <span className="line-clamp-2">{text}</span>
    </Link>
  ),
);

NavigationButton.displayName = 'NavigationButton';
